import TextButton from 'components/Luxkit/Button/TextButton'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import ContactContext from 'contexts/contactContext'
import ModalContext from 'contexts/ModalContext'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import WaitingListRequestFormModal from '../WaitingListRequest/WaitingListRequestFormModal'
import PrivateRequestFormModal from '../PrivateRequest/PrivateRequestFormModal'
import { TOUR_V2_DEFAULT_OCCUPANCIES } from 'constants/tours'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { DepartureSoldOutReason } from 'lib/tours/tourDepartureUtils'
import { isSalesforceTourV2 } from 'lib/offer/offerUtils'
import { isTourLimitedTimeOffer } from 'lib/tours/tourUtils'

const Tile = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  display: grid;
  padding: ${rem(20)};
  gap: ${rem(16)};
  grid-template-areas:
  "dates"
  "call"
  "cta";


  ${mediaQueryUp.tablet} {
    column-gap: ${rem(40)};
    row-gap: ${rem(12)};
    padding: ${rem(16)} 0;
    grid-template-columns: 1fr min-content;
    grid-template-areas:
    "dates cta"
    "call cta";
    align-items: center;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: ${rem(500)} ${rem(210)} 1fr;
    grid-template-areas: "dates call cta";
  }
`

interface Props {
  offer: App.Tours.TourV2Offer;
  travellers?: Array<App.Occupants>;
  reason: DepartureSoldOutReason;
  onUpdateTravelers?: () => void;
}

function TourV2DeparturesSoldOutTile(props: Props) {
  const {
    offer,
    travellers = TOUR_V2_DEFAULT_OCCUPANCIES,
    reason,
    onUpdateTravelers,
  } = props

  const contact = useContext(ContactContext)
  const showModal = useContext(ModalContext)

  const onJoinWaitlist = useCallback(() => {
    showModal(<WaitingListRequestFormModal offer={offer} />)
  }, [offer, showModal])

  const onRequestPrivateDeparture = useCallback(() => {
    showModal(<PrivateRequestFormModal
      offer={offer}
      defaultTravellers={travellers}
    />)
  }, [travellers, offer, showModal])

  return <Tile >
    <div
      style={{ gridArea: 'dates' }}
    >
      <Heading variant="heading4">No departures available</Heading>
      <BodyText variant="medium">
        {reason === 'travellers' && <>
          There are no departures matching your traveller selection of {formatOccupantsShort(travellers)}
        </>}
        {reason === 'sold-out' && <>Sorry, this offer has sold out!</>}
      </BodyText>
    </div>
    <div style={{ gridArea: 'call' }}>
      <BodyText variant="medium" >
        Give our team a call on{' '}
        <TextLink wrap="no-wrap" href={`tel:${contact.defaultContact.number}`}>
          {contact.defaultContact.humanReadable}
        </TextLink>{' '}
        for more tour options
        {reason === 'travellers' && <>
          {' '}or <TextLink onClick={onUpdateTravelers}>Update travellers</TextLink>
        </>}
      </BodyText>
    </div>
    <Group
      direction="horizontal"
      gap={8}
      tabletHorizontalAlign="end"
      horizontalAlign="stretch"
      verticalAlign="center"
      style={{ gridArea: 'cta' }}
    >
      {isSalesforceTourV2(offer) && !isTourLimitedTimeOffer(offer) && <TextButton kind="tertiary" onClick={onRequestPrivateDeparture}>Request a private departure</TextButton>}
      <TextButton kind="secondary" onClick={onJoinWaitlist}>Join waitlist</TextButton>
    </Group>
  </Tile>
}

type MappedProps = Pick<Props, 'offer' | 'travellers'>

export default withOfferPageState((state): MappedProps => ({
  offer: state.tourV2Offer!,
  travellers: state.rooms,
}), TourV2DeparturesSoldOutTile)
